<script setup>
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'
import { useAuth } from '@/composables/auth/useAuth'
import { useThemeStore } from '@/stores/themeStore'
import { useNavBadges } from '@/composables/ui/useNavBadges'
import { useSyncRefresh } from '@/composables/data/useSyncRefresh.js'
import ToastContainer from '@/components/ui/ToastContainer.vue'
import {
  UserCircleIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  SunIcon,
  MoonIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/vue/24/outline'

const { user, isAdmin, hasDashboardAccess, logout } = useAuth()
const themeStore = useThemeStore()
const route = useRoute()
const { beoordelenCount, concretiserenCount, doenCount } = useNavBadges()
useSyncRefresh()

// Mobile menu state
const isMobileMenuOpen = ref(false)

// Close mobile menu on route change
watch(
  () => route.path,
  () => {
    isMobileMenuOpen.value = false
  }
)

const handleLogout = async () => {
  await logout()
  window.location.href = '/login'
}
</script>

<template>
  <div id="app" class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <nav class="bg-gray-800">
      <div class="flex items-center h-16 px-4">
        <!-- Logo - hidden between md and lg to save space, visible on mobile and lg+ -->
        <router-link
          to="/"
          class="flex-shrink-0 hover:opacity-90 transition-opacity md:hidden lg:block lg:mr-8"
        >
          <img src="/logo.webp" alt="Frisse Blikken" class="h-8" />
        </router-link>

        <!-- Mobile hamburger button -->
        <button
          @click="isMobileMenuOpen = !isMobileMenuOpen"
          class="md:hidden ml-auto p-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
          aria-label="Menu openen"
        >
          <Bars3Icon v-if="!isMobileMenuOpen" class="h-6 w-6" />
          <XMarkIcon v-else class="h-6 w-6" />
        </button>

        <!-- Desktop menu items (hidden on mobile) -->
        <div class="hidden md:flex items-center space-x-4 lg:space-x-8 ml-auto">
          <router-link
            to="/aanvragen"
            class="text-gray-300 hover:text-white px-2 lg:px-3 py-2 rounded-md text-xs lg:text-sm font-medium transition-colors"
            active-class="text-white bg-gray-700"
          >
            Aanvragen
          </router-link>
          <router-link
            to="/beoordelen"
            class="relative text-gray-300 hover:text-white px-2 lg:px-3 py-2 rounded-md text-xs lg:text-sm font-medium transition-colors"
            active-class="text-white bg-gray-700"
          >
            Beoordelen
            <span
              v-if="beoordelenCount > 0"
              class="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white"
            >
              {{ beoordelenCount > 99 ? '99+' : beoordelenCount }}
            </span>
          </router-link>
          <router-link
            to="/concretiseren"
            class="relative text-gray-300 hover:text-white px-2 lg:px-3 py-2 rounded-md text-xs lg:text-sm font-medium transition-colors"
            active-class="text-white bg-gray-700"
          >
            Concretiseren
            <span
              v-if="concretiserenCount > 0"
              class="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue-500 text-[10px] font-bold text-white"
            >
              {{ concretiserenCount > 99 ? '99+' : concretiserenCount }}
            </span>
          </router-link>
          <router-link
            to="/doen"
            class="relative text-gray-300 hover:text-white px-2 lg:px-3 py-2 rounded-md text-xs lg:text-sm font-medium transition-colors"
            active-class="text-white bg-gray-700"
          >
            Doen
            <span
              v-if="doenCount > 0"
              class="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-orange-500 text-[10px] font-bold text-white"
            >
              {{ doenCount > 99 ? '99+' : doenCount }}
            </span>
          </router-link>
          <div class="h-6 w-px bg-gray-600"></div>
          <router-link
            v-if="hasDashboardAccess"
            to="/"
            class="text-gray-300 hover:text-white px-2 lg:px-3 py-2 rounded-md text-xs lg:text-sm font-medium transition-colors"
            active-class="text-white bg-gray-700"
          >
            Dashboard
          </router-link>
          <router-link
            to="/medewerkers"
            class="text-gray-300 hover:text-white px-2 lg:px-3 py-2 rounded-md text-xs lg:text-sm font-medium transition-colors"
            active-class="text-white bg-gray-700"
          >
            Medewerkers
          </router-link>
          <router-link
            to="/wzt"
            class="text-gray-300 hover:text-white px-2 lg:px-3 py-2 rounded-md text-xs lg:text-sm font-medium transition-colors"
            active-class="text-white bg-gray-700"
          >
            WZT
          </router-link>
          <!-- User dropdown menu -->
          <Menu v-if="user" as="div" class="relative pl-4 border-l border-gray-600">
            <MenuButton
              class="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <img
                v-if="user.picture"
                :src="user.picture"
                :alt="user.name"
                class="h-8 w-8 rounded-full"
              />
              <UserCircleIcon v-else class="h-8 w-8 text-gray-300" />
              <span class="hidden lg:inline text-sm font-medium text-gray-200">{{
                user.name
              }}</span>
              <ChevronDownIcon class="h-4 w-4 text-gray-400" />
            </MenuButton>

            <transition
              enter-active-class="transition duration-100 ease-out"
              enter-from-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-75 ease-in"
              leave-from-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <MenuItems
                class="absolute right-0 mt-2 w-48 origin-top-right rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 focus:outline-none z-50"
              >
                <div class="py-1">
                  <!-- Admin link (only for admins) -->
                  <MenuItem v-if="isAdmin" v-slot="{ active }">
                    <router-link
                      to="/admin"
                      :class="[
                        active ? 'bg-gray-100 dark:bg-gray-700' : '',
                        'flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200'
                      ]"
                    >
                      <Cog6ToothIcon class="h-4 w-4" />
                      Beheer
                    </router-link>
                  </MenuItem>

                  <div
                    v-if="isAdmin"
                    class="border-t border-gray-100 dark:border-gray-700 my-1"
                  ></div>

                  <!-- Theme toggle -->
                  <MenuItem v-slot="{ active }">
                    <button
                      @click="themeStore.toggleTheme()"
                      :class="[
                        active ? 'bg-gray-100 dark:bg-gray-700' : '',
                        'flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200'
                      ]"
                    >
                      <SunIcon v-if="themeStore.isDark" class="h-4 w-4" />
                      <MoonIcon v-else class="h-4 w-4" />
                      {{ themeStore.isDark ? 'Light mode' : 'Dark mode' }}
                    </button>
                  </MenuItem>

                  <div class="border-t border-gray-100 dark:border-gray-700 my-1"></div>

                  <!-- Logout -->
                  <MenuItem v-slot="{ active }">
                    <button
                      @click="handleLogout"
                      :class="[
                        active ? 'bg-gray-100 dark:bg-gray-700' : '',
                        'flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200'
                      ]"
                    >
                      <ArrowRightOnRectangleIcon class="h-4 w-4" />
                      Uitloggen
                    </button>
                  </MenuItem>
                </div>
              </MenuItems>
            </transition>
          </Menu>
        </div>
      </div>

      <!-- Mobile menu overlay -->
      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div v-if="isMobileMenuOpen" class="md:hidden fixed inset-0 z-50">
          <!-- Backdrop -->
          <div class="fixed inset-0 bg-black/50" @click="isMobileMenuOpen = false" />

          <!-- Slide-in panel -->
          <transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="translate-x-full"
            enter-to-class="translate-x-0"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="translate-x-0"
            leave-to-class="translate-x-full"
            appear
          >
            <div
              v-if="isMobileMenuOpen"
              class="fixed right-0 top-0 h-full w-72 bg-gray-800 shadow-xl flex flex-col"
            >
              <!-- Mobile menu header -->
              <div class="flex items-center justify-between h-16 px-4 border-b border-gray-700">
                <span class="text-white font-medium">Menu</span>
                <button
                  @click="isMobileMenuOpen = false"
                  class="p-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-md"
                >
                  <XMarkIcon class="h-6 w-6" />
                </button>
              </div>

              <!-- Mobile nav links -->
              <div class="flex-1 overflow-y-auto py-4">
                <router-link
                  to="/aanvragen"
                  class="block px-4 py-3 text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
                  active-class="text-white bg-gray-700"
                >
                  Aanvragen
                </router-link>
                <router-link
                  to="/beoordelen"
                  class="flex items-center justify-between px-4 py-3 text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
                  active-class="text-white bg-gray-700"
                >
                  <span>Beoordelen</span>
                  <span
                    v-if="beoordelenCount > 0"
                    class="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white"
                  >
                    {{ beoordelenCount > 99 ? '99+' : beoordelenCount }}
                  </span>
                </router-link>
                <router-link
                  to="/concretiseren"
                  class="flex items-center justify-between px-4 py-3 text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
                  active-class="text-white bg-gray-700"
                >
                  <span>Concretiseren</span>
                  <span
                    v-if="concretiserenCount > 0"
                    class="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-blue-500 px-1.5 text-[11px] font-bold text-white"
                  >
                    {{ concretiserenCount > 99 ? '99+' : concretiserenCount }}
                  </span>
                </router-link>
                <router-link
                  to="/doen"
                  class="flex items-center justify-between px-4 py-3 text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
                  active-class="text-white bg-gray-700"
                >
                  <span>Doen</span>
                  <span
                    v-if="doenCount > 0"
                    class="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-orange-500 px-1.5 text-[11px] font-bold text-white"
                  >
                    {{ doenCount > 99 ? '99+' : doenCount }}
                  </span>
                </router-link>
                <div class="border-t border-gray-700 my-2"></div>
                <router-link
                  v-if="hasDashboardAccess"
                  to="/"
                  class="block px-4 py-3 text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
                  active-class="text-white bg-gray-700"
                >
                  Dashboard
                </router-link>
                <router-link
                  to="/medewerkers"
                  class="block px-4 py-3 text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
                  active-class="text-white bg-gray-700"
                >
                  Medewerkers
                </router-link>
                <router-link
                  to="/wzt"
                  class="block px-4 py-3 text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
                  active-class="text-white bg-gray-700"
                >
                  WZT
                </router-link>

                <!-- Admin link (only for admins) -->
                <router-link
                  v-if="isAdmin"
                  to="/admin"
                  class="block px-4 py-3 text-gray-300 hover:text-white hover:bg-gray-700 transition-colors border-t border-gray-700 mt-2"
                  active-class="text-white bg-gray-700"
                >
                  <div class="flex items-center gap-2">
                    <Cog6ToothIcon class="h-5 w-5" />
                    Beheer
                  </div>
                </router-link>
              </div>

              <!-- Mobile menu footer with user info -->
              <div v-if="user" class="border-t border-gray-700 p-4">
                <div class="flex items-center gap-3 mb-3">
                  <img
                    v-if="user.picture"
                    :src="user.picture"
                    :alt="user.name"
                    class="h-10 w-10 rounded-full"
                  />
                  <UserCircleIcon v-else class="h-10 w-10 text-gray-300" />
                  <div class="flex-1 min-w-0">
                    <div class="text-sm font-medium text-white truncate">{{ user.name }}</div>
                    <div class="text-xs text-gray-400 truncate">{{ user.email }}</div>
                  </div>
                </div>

                <div class="flex items-center gap-2">
                  <!-- Theme toggle -->
                  <button
                    @click="themeStore.toggleTheme()"
                    class="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
                  >
                    <SunIcon v-if="themeStore.isDark" class="h-5 w-5" />
                    <MoonIcon v-else class="h-5 w-5" />
                    <span class="text-sm">{{ themeStore.isDark ? 'Light' : 'Dark' }}</span>
                  </button>

                  <!-- Logout button -->
                  <button
                    @click="handleLogout"
                    class="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
                  >
                    <ArrowRightOnRectangleIcon class="h-5 w-5" />
                    <span class="text-sm">Uitloggen</span>
                  </button>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </transition>
    </nav>

    <main class="max-w-[90rem] mx-auto sm:pt-6 sm:px-6 lg:px-8">
      <router-view v-slot="{ Component, route }">
        <component :is="Component" :key="route.path" />
      </router-view>
    </main>

    <!-- Global toast notifications -->
    <ToastContainer />
  </div>
</template>

<style scoped></style>
